import { useMemo, useState, useEffect } from "react";
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxItem, ComboboxList } from "@/components/ui/combobox";
import { Label } from "@/components/ui/label";
import { useGetCarriersQuery } from "@/hooks/queries/organization";

interface CarrierSelectProps {
  value: string | undefined;
  onChange: (value: string | undefined) => void;
}

export const CarrierSelect = ({ value, onChange }: CarrierSelectProps) => {
  const { data: carriers, isLoading } = useGetCarriersQuery();

  const options = useMemo(
    () =>
      (carriers ?? [])
        .map((carrier) => ({
          value: carrier.id,
          label: carrier.name,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [carriers],
  );

  const selectedOption = useMemo(() => options.find((opt) => opt.value === value), [options, value]);

  const [inputValue, setInputValue] = useState(selectedOption?.label ?? "");

  // Sync inputValue with selectedOption when value prop changes
  useEffect(() => {
    setInputValue(selectedOption?.label ?? "");
  }, [selectedOption?.label]);

  return (
    <div className="flex flex-col items-start">
      <Label>Carrier</Label>
      <div className="relative w-56">
        <Combobox
          items={options}
          itemToStringValue={(option) => option.label}
          value={selectedOption}
          onValueChange={(item) => {
            if (item) {
              onChange(item.value);
            } else {
              onChange(undefined);
            }
          }}
          inputValue={inputValue}
          onInputValueChange={setInputValue}
        >
          <ComboboxInput placeholder={isLoading ? "Loading..." : "Select..."} showClear={!!value} />
          <ComboboxContent>
            <ComboboxEmpty>No results found</ComboboxEmpty>
            <ComboboxList>
              {(option) => (
                <ComboboxItem key={option.value} value={option}>
                  {option.label}
                </ComboboxItem>
              )}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </div>
    </div>
  );
};
